import api from './api';

export interface FeatureToggle {
  code: string;
  name: string;
  description: string;
  category: string;
  is_core_feature: boolean;
  enabled: boolean;
  access_level?: string | null;
  org_enabled?: boolean;
  locked_reason?: string | null;
  domain_locked?: boolean;
  domain_locked_reason?: string | null;
  // Triple-check permit system (from UserFeatureEnablement)
  org_permit?: boolean;
  domain_permit?: boolean;
  user_permit?: boolean;
  has_access?: boolean; // Based on current user type
  user_has_access?: boolean; // User-specific access (all 3 permits checked) - triple check system
}

export interface FeatureListResponse {
  organization?: {
    id: number;
    name: string;
    code: string;
  };
  domain?: {
    id: number;
    name: string;
    code: string;
  };
  user?: {
    id: number;
    username: string;
    full_name: string;
  };
  features: FeatureToggle[];
}

/**
 * Format error message for feature enablement failures.
 * When the API returns missing_dependencies (code: "missing_dependencies"),
 * includes detail, list of missing feature names, and how_to_fix.
 */
export function getFeatureEnablementErrorMessage(error: unknown, fallback: string): string {
  const anyError = error as { response?: { data?: Record<string, unknown> }; message?: string };
  const data = anyError?.response?.data;
  if (!data || typeof data !== 'object') {
    return (anyError?.message as string) || fallback;
  }
  const detail = typeof data.detail === 'string' ? data.detail : '';
  if (data.code === 'missing_dependencies' && Array.isArray(data.missing_dependencies)) {
    const names = (data.missing_dependencies as { name?: string }[])
      .map((d) => d.name)
      .filter(Boolean)
      .join(', ');
    const how = typeof data.how_to_fix === 'string' ? data.how_to_fix : '';
    return [detail, names ? `Missing: ${names}.` : '', how].filter(Boolean).join(' ');
  }
  return detail || (data.error as string) || (anyError?.message as string) || fallback;
}

const featureEnablementService = {
  async getOrganizationFeatures(organizationId: number): Promise<FeatureListResponse> {
    const response = await api.get(`feature-enablement/organizations/${organizationId}/features/`);
    return response as FeatureListResponse;
  },

  async enableOrganizationFeature(organizationId: number, featureCode: string, notes?: string) {
    await api.post(`feature-enablement/organizations/${organizationId}/features/`, {
      feature_code: featureCode,
      notes,
    });
  },

  async disableOrganizationFeature(organizationId: number, featureCode: string) {
    await api.delete(`feature-enablement/organizations/${organizationId}/features/${featureCode}/`);
  },

  async getDomainFeatures(domainId: number): Promise<FeatureListResponse> {
    const response = await api.get(`feature-enablement/domains/${domainId}/features/`);
    return response as FeatureListResponse;
  },

  async enableDomainFeature(
    domainId: number,
    featureCode: string,
    enabled: boolean,
    notes?: string
  ) {
    const response = await api.post(`feature-enablement/domains/${domainId}/features/`, {
      feature_code: featureCode,
      enabled,
      notes,
    });
    return response;
  },
  
  async bulkGrantUserPermit(
    featureCode: string,
    userIds: number[],
    permitType?: 'org_permit' | 'domain_permit' | 'user_permit',
    hospitalId?: number
  ) {
    const body: any = {
      feature_code: featureCode,
      user_ids: userIds,
      action: 'grant',
    };
    if (permitType) {
      body.permit_type = permitType;
    }
    if (hospitalId) {
      body.hospital_id = hospitalId;
    }
    const response = await api.post(`feature-enablement/users/features/`, body);
    return response;
  },

  async bulkRevokeUserPermit(
    featureCode: string,
    userIds: number[],
    permitType: 'org_permit' | 'domain_permit' | 'user_permit',
    hospitalId?: number
  ) {
    const body: any = {
      feature_code: featureCode,
      user_ids: userIds,
      permit_type: permitType,
      action: 'revoke',
    };
    if (hospitalId) {
      body.hospital_id = hospitalId;
    }
    const response = await api.post(`feature-enablement/users/features/`, body);
    return response;
  },
  
  async getUsersForFeature(
    hospitalId: number,
    featureCode?: string,
    roleSpecialtyId?: number
  ) {
    const params = new URLSearchParams();
    params.append('hospital_id', String(hospitalId));
    if (featureCode) params.append('feature_code', featureCode);
    if (roleSpecialtyId) params.append('role_specialty_id', String(roleSpecialtyId));
    
    const response = await api.get(`feature-enablement/users/features/?${params.toString()}`);
    return response;
  },

  async disableDomainFeature(domainId: number, featureCode: string) {
    await api.delete(`feature-enablement/domains/${domainId}/features/${featureCode}/`);
  },

  async getUserFeatures(userId: number): Promise<FeatureListResponse> {
    const response = await api.get(`feature-enablement/users/${userId}/features/`);
    return response as FeatureListResponse;
  },

  async enableUserFeature(
    userId: number,
    featureCode: string,
    accessLevel: string,
    notes?: string,
    accessExpiresAt?: string | null,
  ) {
    await api.post(`feature-enablement/users/${userId}/features/`, {
      feature_code: featureCode,
      access_level: accessLevel,
      notes,
      access_expires_at: accessExpiresAt,
    });
  },

  async disableUserFeature(userId: number, featureCode: string) {
    await api.delete(`feature-enablement/users/${userId}/features/${featureCode}/`);
  },

  /**
   * @param options.skipAuthFailure - If true, 401 will not trigger global refresh/logout (e.g. for background polling).
   */
  async getMyFeatures(options?: { skipAuthFailure?: boolean }): Promise<FeatureListResponse> {
    const config = options?.skipAuthFailure ? { _skipAuthFailure: true } : undefined;
    const response = await api.get(`feature-enablement/my-features/?_t=${Date.now()}`, config);
    return response as FeatureListResponse;
  },

  async getAvailableFeaturesForGrant(hospitalId: number): Promise<{
    features: FeatureToggle[];
    grouped_by_category: Record<string, FeatureToggle[]>;
    hospital: { id: number; name: string; code: string };
  }> {
    const params = new URLSearchParams();
    params.append('hospital_id', String(hospitalId));
    const response = await api.get(`feature-enablement/available-for-grant/?${params.toString()}`);
    return response as {
      features: FeatureToggle[];
      grouped_by_category: Record<string, FeatureToggle[]>;
      hospital: { id: number; name: string; code: string };
    };
  },

  async grantDomainPermit(domainId: number, featureCode: string) {
    const response = await api.post(
      `feature-enablement/domains/${domainId}/features/${featureCode}/domain-permit/`,
      { action: 'grant' }
    );
    return response;
  },

  async revokeDomainPermit(domainId: number, featureCode: string) {
    const response = await api.post(
      `feature-enablement/domains/${domainId}/features/${featureCode}/domain-permit/`,
      { action: 'revoke' }
    );
    return response;
  },

  async getDomainPermitStatus(domainId: number, featureCode: string) {
    const response = await api.get(
      `feature-enablement/domains/${domainId}/features/${featureCode}/domain-permit/`
    );
    return response;
  },

  // Template methods
  async getTemplates(filters?: { template_type?: string; is_active?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.template_type) params.append('template_type', filters.template_type);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    
    const query = params.toString();
    const response = await api.get(`feature-enablement/templates/${query ? `?${query}` : ''}`);
    return response;
  },

  async getTemplate(templateId: number) {
    const response = await api.get(`feature-enablement/templates/${templateId}/`);
    return response;
  },

  async createTemplate(templateData: any) {
    const response = await api.post('feature-enablement/templates/', templateData);
    return response;
  },

  async updateTemplate(templateId: number, templateData: any) {
    const response = await api.put(`feature-enablement/templates/${templateId}/`, templateData);
    return response;
  },

  async deleteTemplate(templateId: number) {
    await api.delete(`feature-enablement/templates/${templateId}/`);
  },

  async applyTemplate(templateId: number, userIds: number[]) {
    const response = await api.post(`feature-enablement/templates/${templateId}/apply-to-users/`, {
      user_ids: userIds,
    });
    return response;
  },

  async getApplicableUsers(templateId: number) {
    const response = await api.get(`feature-enablement/templates/${templateId}/applicable-users/`);
    return response;
  },

  // Audit methods
  async getAuditLog(filters?: {
    user_id?: number;
    feature_code?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
    organization_id?: number;
    domain_id?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.user_id) params.append('user_id', String(filters.user_id));
    if (filters?.feature_code) params.append('feature_code', filters.feature_code);
    if (filters?.action) params.append('action', filters.action);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.organization_id) params.append('organization_id', String(filters.organization_id));
    if (filters?.domain_id) params.append('domain_id', String(filters.domain_id));

    const query = params.toString();
    const response = await api.get(`feature-enablement/audit/${query ? `?${query}` : ''}`);
    return response;
  },

  async getAuditStats() {
    const response = await api.get('feature-enablement/audit/summary-stats/');
    return response;
  },

  async grantUserPermit(
    userId: number,
    featureCode: string,
    permitType: 'org_permit' | 'domain_permit' | 'user_permit',
    hospitalId?: number | null
  ) {
    const response = await api.post(
      `feature-enablement/users/${userId}/features/${featureCode}/permit/`,
      { permit_type: permitType, action: 'grant', ...(hospitalId ? { hospital_id: hospitalId } : {}) }
    );
    return response;
  },

  async revokeUserPermit(
    userId: number,
    featureCode: string,
    permitType: 'org_permit' | 'domain_permit' | 'user_permit',
    hospitalId?: number | null
  ) {
    const response = await api.post(
      `feature-enablement/users/${userId}/features/${featureCode}/permit/`,
      { permit_type: permitType, action: 'revoke', ...(hospitalId ? { hospital_id: hospitalId } : {}) }
    );
    return response;
  },
};

export default featureEnablementService;

